import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["media", "text", "canvas"]

  static values = {
    images: Array
  }

  async connect() {
    this.currentIndex = -1
    this.ready = false
    this.playing = false

    this.hydra = null
    this.hydraActive = false

    /*
     * Efecto Hydra que está actualmente activo.
     */
    this.currentHydraEffect = null

    /*
     * Editor Hydra.
     *
     * El textarea está fuera del elemento Stimulus,
     * por eso lo buscamos directamente por ID.
     */
    this.editor = document.getElementById("asemic")

    this.editorMode = false

    /*
     * Crear botón secreto.
     */
    this.createHydraButton()

    /*
     * Eventos del editor.
     */
    this.handleEditorKeydown =
      this.handleEditorKeydown.bind(this)

    this.handleMouseMove =
      this.handleMouseMove.bind(this)

    if (this.editor) {
      this.editor.addEventListener(
        "keydown",
        this.handleEditorKeydown
      )
    }

    window.addEventListener(
      "mousemove",
      this.handleMouseMove
    )

    /*
     * Slides
     */
    this.slides = (this.imagesValue || [])
      .filter(Boolean)
      .map(slide => ({
        ...slide
      }))

    this.startLoader()
    await this.preloadMedia()
    this.stopLoader()

    this.ready = true

    /*
     * Teclado:
     *
     * ↑ = play / pausa
     * ← = anterior
     * → = siguiente
     */
    this.handleKeydown =
      this.handleKeydown.bind(this)

    window.addEventListener(
      "keydown",
      this.handleKeydown
    )

    if (this.slides.length) {
      this.currentIndex = 0

      this.textTarget.textContent =
        this.slides[0].text || ""

      this.showSlide(0)

      /*
       * El primer slide comienza
       * reproduciéndose automáticamente.
       */
      this.play()
    }
  }

  disconnect() {
    this.stopLoader()
    this.stopHydra()

    window.removeEventListener(
      "keydown",
      this.handleKeydown
    )

    window.removeEventListener(
      "mousemove",
      this.handleMouseMove
    )

    if (this.editor) {
      this.editor.removeEventListener(
        "keydown",
        this.handleEditorKeydown
      )
    }

    if (this.hydraButton) {
      this.hydraButton.remove()
    }

    this.pauseMedia()
  }

  /*
   * ==========================================
   * PRELOAD
   * ==========================================
   */

  async preloadMedia() {
    const promises =
      this.mediaTargets.map(element => {
        return new Promise(resolve => {

          if (
            element.tagName === "VIDEO"
          ) {
            element.preload = "auto"

            element.addEventListener(
              "loadeddata",
              resolve,
              { once: true }
            )

            element.addEventListener(
              "error",
              resolve,
              { once: true }
            )

            element.load()

          } else {

            if (element.complete) {
              resolve()
            } else {
              element.onload = resolve
              element.onerror = resolve
            }
          }
        })
      })

    await Promise.all(promises)
  }

  /*
   * ==========================================
   * PLAY / PAUSA
   * ==========================================
   */

  start() {
    if (
      !this.ready ||
      !this.slides.length
    ) {
      return
    }

    if (this.playing) {
      this.pause()
    } else {
      this.play()
    }
  }

  play() {
    if (this.playing)
      return

    this.playing = true

    this.playCurrentMedia()
  }

  pause() {
    if (!this.playing)
      return

    this.playing = false

    this.pauseMedia()
  }

  /*
   * ==========================================
   * TECLADO
   * ==========================================
   */

  handleKeydown(event) {
    if (!this.ready)
      return

    const tag =
      event.target.tagName.toLowerCase()

    /*
     * No interceptar las flechas dentro
     * de campos de formulario.
     */
    if (
      tag === "input" ||
      tag === "textarea" ||
      tag === "select"
    ) {
      return
    }

    switch (event.key) {

      case "ArrowUp":
        event.preventDefault()
        this.start()
        break

      case "ArrowRight":
        event.preventDefault()
        this.next()
        break

      case "ArrowLeft":
        event.preventDefault()
        this.previous()
        break
    }
  }

  /*
   * ==========================================
   * SIGUIENTE
   * ==========================================
   */

  next() {
    if (!this.ready)
      return

    const nextIndex =
      this.currentIndex + 1

    if (
      nextIndex >=
      this.slides.length
    ) {
      return
    }

    this.showSlide(nextIndex)
  }

  /*
   * ==========================================
   * ANTERIOR
   * ==========================================
   */

  previous() {
    if (!this.ready)
      return

    const previousIndex =
      this.currentIndex - 1

    if (previousIndex < 0) {
      return
    }

    this.showSlide(previousIndex)
  }

  /*
   * ==========================================
   * SLIDE
   * ==========================================
   */

  showSlide(index) {
    const slide =
      this.slides[index]

    if (!slide)
      return

    this.renderMedia(index)

    this.textTarget.textContent =
      slide.text || ""

    this.currentIndex = index

    if (this.playing) {
      this.playCurrentMedia()
    }

    /*
     * Si el editor está abierto y el nuevo slide
     * tiene un efecto Hydra, actualizar el código.
     */
    if (
      this.editorMode &&
      slide.type === "canvas" &&
      slide.effect
    ) {
      this.loadHydraEffect(
        slide.effect
      )
    }
  }

  /*
   * ==========================================
   * MEDIA
   * ==========================================
   */

  playCurrentMedia() {
    const slide =
      this.slides[this.currentIndex]

    if (!slide)
      return

    /*
     * Canvas / Hydra no necesita play.
     */
    if (
      slide.type === "canvas"
    ) {
      return
    }

    const media =
      this.getMedia(
        this.currentIndex
      )

    if (!media)
      return

    if (
      media.tagName === "VIDEO"
    ) {
      media.loop = true

      media.play().catch(
        console.error
      )
    }
  }

  pauseMedia() {
    this.mediaTargets.forEach(
      element => {

        if (
          element.tagName === "VIDEO"
        ) {
          element.pause()
        }

      }
    )
  }

  /*
   * ==========================================
   * RENDER MEDIA
   * ==========================================
   */

  renderMedia(index) {
    const slide =
      this.slides[index]

    if (!slide)
      return

    this.mediaTargets.forEach(
      element => {

        element.classList.add(
          "d-none"
        )

        if (
          element.tagName === "VIDEO"
        ) {
          element.pause()
          element.loop = false
          element.currentTime = 0
        }

      }
    )

    /*
     * CANVAS / HYDRA
     */
    if (
      slide.type === "canvas"
    ) {
      this.canvasTarget.classList.remove(
        "d-none"
      )

      this.startHydra(
        slide.effect
      )

      return
    }

    /*
     * MEDIA NORMAL
     */
    this.stopHydra()

    this.canvasTarget.classList.add(
      "d-none"
    )

    const media =
      this.getMedia(index)

    if (!media)
      return

    media.classList.remove(
      "d-none"
    )

    /*
     * VIDEO VISIBLE
     */
    if (
      media.tagName === "VIDEO"
    ) {
      media.currentTime = 0
      media.loop = true

      if (this.playing) {
        media.play().catch(
          console.error
        )
      }
    }
  }

  getMedia(index) {
    return this.mediaTargets.find(
      element =>
        Number(
          element.dataset.mediaIndex
        ) === index
    )
  }

  /*
   * ==========================================
   * HYDRA
   * ==========================================
   */

  initHydra() {
    if (this.hydra)
      return true

    if (!window.Hydra) {
      console.error(
        "Hydra no está cargado"
      )

      return false
    }

    const canvas =
      this.canvasTarget.querySelector(
        "canvas"
      )

    if (!canvas)
      return false

    this.hydra = new Hydra({
      canvas: canvas,
      detectAudio: false,
      makeGlobal: true
    })

    return true
  }

  /*
   * ==========================================
   * HYDRA - FLOR
   * ==========================================
   */

  hydraFlor() {
    voronoi(8, 1)
      .mult(
        osc(
          10,
          0.1,
          () => Math.sin(time) * 3
        )
        .saturate(3)
        .kaleid(200)
      )
      .modulate(o0, 0.5)
      .add(o0, 0.8)
      .scrollY(-0.01)
      .scale(0.99)
      .modulate(
        voronoi(8, 1),
        0.008
      )
      .luma(0.3)
      .out()
  }

  /*
   * ==========================================
   * HYDRA - ROT
   * ==========================================
   */

  hydraRot() {
    osc(3, 0.01, 1.3)
      .luma(0.6)
      .color(
        0.75,
        0.27,
        0.8
      )
      .modulateRotate(
        noise(1, 0.03),
        0.7
      )
      .modulateHue(
        osc(
          7,
          0.01,
          1.3
        ),
        77
      )
      .modulateRotate(
        noise(
          1.8,
          0.04
        ),
        0.84
      )
      .modulateHue(
        noise(2)
          .diff(o0)
          .modulateScale(
            o0,
            0.11
          )
          .rotate(Math.PI),
        10
      )
      .modulateScale(
        noise(2)
          .modulateRotate(
            o0,
            0.31
          )
          .rotate(Math.PI),
        0.1
      )
      .mult(
        gradient(0.21),
        0.14
      )
      .hue(0.8)
      .saturate(0.9)
      .modulate(
        noise(
          0.1,
          0.03
        ),
        3.1
      )
      .out(o0)
  }

  /*
   * ==========================================
   * HYDRA - MAR
   * ==========================================
   */

  hydraMar() {
    osc(10, 0, 200)
      .add(
        voronoi(1, 0.8)
      )
      .diff(
        src(o0).scale(1.9)
      )
      .modulate(
        o0,
        0.2
      )
      .out(o0)
  }

  /*
   * ==========================================
   * HYDRA - LAGO SYNTH
   * ==========================================
   */

  hydraLagosynth() {
    s0.initImage(
      "/public/sync/IMG_3393.jpg"
    )

    src(s0)
      .modulate(
        noise(8, 0.2)
          .pixelate(
            () => mouse.x * 0.1,
            0.5
          )
          .scale(1, 3),
        () => mouse.y * 0.00095
      )
      .out()
  }

  /*
   * ==========================================
   * HYDRA - LAGO DUST
   * ==========================================
   */

  hydraLagodust() {
    s0.initImage(
      "/public/sync/IMG_6814.jpg"
    )

    src(s0)
      .modulate(
        o0,
        () => mouse.x * 0.001
      )
      .scroll(
        () => mouse.x * 0.001,
        0
      )
      .scrollY(
        -2,
        () => mouse.x * 0.000002
      )
      .out()
  }

  /*
   * ==========================================
   * HYDRA - LAGO ESPEJO
   * ==========================================
   */

hydraLagoespejo() {
      s0.initImage(
        "/public/sync/IMG_3231.JPG"
      );
      src(s0).pixelate(
        () => mouse.y,
        () => mouse.x
      )
.colorama(0.2).modulate(s0,() => mouse.y/1000).out();
    }

  /*
   * ==========================================
   * HYDRA - LAGO GLITCH
   * ==========================================
   */

  hydraLagoglitch() {
    s0.initImage(
      "/public/sync/IMG_0100.JPG"
    )

    const mx = () =>
      (
        mouse.x -
        window.innerWidth / 2
      ) /
      window.innerWidth

    const my = () =>
      (
        mouse.y -
        window.innerHeight / 2
      ) /
      window.innerHeight

    const distance = () =>
      Math.min(
        1,
        Math.sqrt(
          mx() * mx() +
          my() * my()
        ) * 2
      )

    src(s0)
      .scale(() =>
        1.01 +
        Math.abs(mx()) * 0.04 +
        Math.abs(my()) * 0.04
      )
      .scrollX(
        () => mx() * 0.02
      )
      .scrollY(
        () => my() * 0.02
      )
      .contrast(
        () =>
          1 + distance() * 2
      )
      .saturate(
        () =>
          1 - distance()
      )
      .blend(
        src(o0)
          .scale(() =>
            1.01 +
            Math.abs(mx()) * 0.01 +
            Math.abs(my()) * 0.01
          )
          .scrollX(
            () => mx() * 0.01
          )
          .scrollY(
            () => my() * 0.01
          )
          .contrast(
            () =>
              0.5 +
              distance() * 2
          )
          .saturate(
            () =>
              0.5 -
              distance()
          ),
        () =>
          0.5 +
          distance() * 0.5
      )
      .out(o0)
  }

  /*
   * ==========================================
   * HYDRA - LLUVIA HERMOSA
   * ==========================================
   */

  hydraLluviahermosa() {
    osc(80, 0, 2)
      .modulatePixelate(
        noise(10, 0)
          .pixelate(2, 160),
        24,
        6
      )
      .scrollX(1, 0.05)
      .rotate(-2)
      .modulate(
        o0,
        0.03
      )
      .blend(o0)
      .out()
  }

  /*
   * ==========================================
   * HYDRA - COLOR LOVERS
   * ==========================================
   */

  hydraColorlovers() {
    osc(10, 0.1, -0.005)
      .blend(
        o0,
        0.2
      )
      .modulate(
        shape(2)
          .pixelate(
            200,
            20
          )
          .rotate(
            1.8,
            0.1
          )
      )
      .modulateScale(
        osc(
          15,
          1 *
          Math.sin(
            2 * 0.1
          )
        )
      )
      .out()
  }

  /*
   * ==========================================
   * START HYDRA
   * ==========================================
   */

  startHydra(effect) {
    if (!this.initHydra())
      return

    this.hydraActive = true
    this.currentHydraEffect = effect

    /*
     * Si veníamos de un hush(), intentamos
     * reactivar Hydra.
     */
    if (
      this.hydra &&
      typeof this.hydra.unhush === "function"
    ) {
      this.hydra.unhush()
    }

    const methodName =
      this.getHydraMethodName(effect)

    if (
      !methodName ||
      typeof this[methodName] !== "function"
    ) {
      console.warn(
        "Efecto Hydra desconocido:",
        effect
      )

      return
    }

    try {
      this[methodName]()
    } catch (error) {
      console.error(
        "Error en efecto Hydra:",
        error
      )
    }

    /*
     * Si el editor está abierto,
     * mostrar automáticamente el código.
     */
    if (this.editorMode) {
      this.loadHydraEffect(effect)
    }
  }

  /*
   * Convierte:
   *
   * lagoglitch
   *
   * en:
   *
   * hydraLagoglitch
   */

  getHydraMethodName(effect) {
    if (!effect)
      return null

    return (
      "hydra" +
      effect.charAt(0).toUpperCase() +
      effect.slice(1)
    )
  }

  /*
   * ==========================================
   * STOP HYDRA
   * ==========================================
   */

  stopHydra() {
    if (!this.hydra)
      return

    this.hydra.hush()

    this.hydraActive = false
  }

  /*
   * ==========================================
   * EDITOR HYDRA
   * ==========================================
   */

  createHydraButton() {
    this.hydraButton =
      document.createElement("button")

    this.hydraButton.type = "button"

    this.hydraButton.textContent = "●"

    this.hydraButton.setAttribute(
      "aria-label",
      "Hydra editor"
    )

    this.hydraButton.style.position =
      "fixed"

    this.hydraButton.style.top =
      "10px"

    this.hydraButton.style.right =
      "10px"

    this.hydraButton.style.width =
      "20px"

    this.hydraButton.style.height =
      "20px"

    this.hydraButton.style.padding =
      "0"

    this.hydraButton.style.margin =
      "0"

    this.hydraButton.style.border =
      "0"

    this.hydraButton.style.background =
      "transparent"

    this.hydraButton.style.color =
      "white"

    this.hydraButton.style.cursor =
      "pointer"

    this.hydraButton.style.opacity =
      "0"

    this.hydraButton.style.pointerEvents =
      "none"

    this.hydraButton.style.zIndex =
      "99999"

    this.hydraButton.style.transition =
      "opacity 0.2s"

    this.hydraButton.addEventListener(
      "click",
      event => {
        event.stopPropagation()
        this.toggleHydraEditor()
      }
    )

    document.body.appendChild(
      this.hydraButton
    )
  }

  handleMouseMove(event) {
    if (!this.hydraButton)
      return

    const distanceFromCorner =
      Math.min(
        event.clientX,
        window.innerWidth -
          event.clientX,
        event.clientY,
        window.innerHeight -
          event.clientY
      )

    /*
     * Solo aparece cerca de una esquina.
     */
    const nearCorner =
      distanceFromCorner < 70

    if (nearCorner) {
      this.hydraButton.style.opacity =
        "0.5"

      this.hydraButton.style.pointerEvents =
        "auto"
    } else {
      this.hydraButton.style.opacity =
        "0"

      this.hydraButton.style.pointerEvents =
        "none"
    }
  }

  toggleHydraEditor() {
    if (!this.editor)
      return

    this.editorMode =
      !this.editorMode

    if (this.editorMode) {
      this.openHydraEditor()
    } else {
      this.closeHydraEditor()
    }
  }

  /*
   * ==========================================
   * ABRIR EDITOR
   * ==========================================
   */

  openHydraEditor() {
    /*
     * Guardamos el estilo original.
     */
    if (!this.editorOriginalClass) {
      this.editorOriginalClass =
        this.editor.className
    }

    /*
     * Intentamos cargar el efecto actual.
     *
     * Si estamos en un canvas usamos ese efecto.
     * Si no, usamos el último efecto Hydra.
     */
    const slide =
      this.slides[this.currentIndex]

    const effect =
      slide?.type === "canvas"
        ? slide.effect
        : this.currentHydraEffect

    if (effect) {
      this.loadHydraEffect(effect)
    }

    /*
     * Modo editor.
     */
    this.editor.classList.remove(
      "mirtha",
      "f-64",
      "text-uppercase"
    )

    this.editor.classList.add(
      "text-monospace"
    )

    this.editor.style.fontSize =
      "20px"

    this.editor.style.lineHeight =
      "1.2"

    this.editor.style.textTransform =
      "downcase"

    this.editor.style.whiteSpace =
      "pre"

    this.editor.style.overflow =
      "auto"

    this.editor.style.zIndex =
      "100000"

    this.editor.focus()

    /*
     * Mostrar canvas.
     */
    this.canvasTarget.classList.remove(
      "d-none"
    )

    /*
     * El botón queda visible mientras
     * estamos en modo editor.
     */
    if (this.hydraButton) {
      this.hydraButton.style.opacity =
        "0.7"

      this.hydraButton.style.pointerEvents =
        "auto"
    }
  }

  /*
   * ==========================================
   * CERRAR EDITOR
   * ==========================================
   */

  closeHydraEditor() {
    if (!this.editor)
      return

    /*
     * Restaurar clases originales.
     */
    if (this.editorOriginalClass) {
      this.editor.className =
        this.editorOriginalClass
    }

    /*
     * Limpiar estilos agregados.
     */
    this.editor.style.fontSize = ""
    this.editor.style.lineHeight = ""
    this.editor.style.textTransform = ""
    this.editor.style.whiteSpace = ""
    this.editor.style.overflow = ""
    this.editor.style.zIndex = ""

    /*
     * Volver al texto de la obra.
     */
    const slide =
      this.slides[this.currentIndex]

    if (slide) {
      this.editor.value =
        slide.text || ""
    }

    /*
     * El canvas vuelve a depender del slide.
     */
    if (
      slide?.type !== "canvas"
    ) {
      this.canvasTarget.classList.add(
        "d-none"
      )
    }

    /*
     * Botón secreto.
     */
    if (this.hydraButton) {
      this.hydraButton.style.opacity =
        "0"

      this.hydraButton.style.pointerEvents =
        "none"
    }
  }

  /*
   * ==========================================
   * CARGAR FUNCIÓN EN TEXTAREA
   * ==========================================
   */

  loadHydraEffect(effect) {
    if (!this.editor)
      return

    const methodName =
      this.getHydraMethodName(effect)

    if (
      !methodName ||
      typeof this[methodName] !== "function"
    ) {
      return
    }

    /*
     * toString() obtiene el código real
     * de la función definida en el controlador.
     */
    this.editor.value =
      this[methodName].toString()

    this.currentHydraEffect =
      effect
  }

  /*
   * ==========================================
   * CTRL + SHIFT + ENTER
   * ==========================================
   */

  handleEditorKeydown(event) {
    if (!this.editorMode)
      return

    if (
      event.ctrlKey &&
      event.shiftKey &&
      event.key === "Enter"
    ) {
      event.preventDefault()
      event.stopPropagation()

      this.runHydraCode(
        this.editor.value
      )
    }
  }

  /*
   * ==========================================
   * EJECUTAR CÓDIGO HYDRA
   * ==========================================
   */

  runHydraCode(code) {
    if (!code)
      return

    /*
     * Aseguramos que exista la instancia.
     */
    if (!this.initHydra())
      return

    /*
     * El canvas debe estar visible.
     */
    this.canvasTarget.classList.remove(
      "d-none"
    )

    this.hydraActive = true

    try {
      /*
       * El textarea contiene una definición
       * de método como:
       *
       * hydraLagoglitch() {
       *   ...
       * }
       *
       * La convertimos temporalmente en
       * un objeto para poder ejecutarla.
       */
      const factory =
        new Function(
          `
          return ({
            ${code}
          })
          `
        )

      const object =
        factory()

      const methodNames =
        Object.keys(object)

      if (!methodNames.length) {
        throw new Error(
          "No se encontró ninguna función Hydra."
        )
      }

      const method =
        object[methodNames[0]]

      if (
        typeof method !== "function"
      ) {
        throw new Error(
          "El código no contiene una función válida."
        )
      }

      /*
       * Ejecutamos la función.
       *
       * Como Hydra fue inicializado con
       * makeGlobal: true, las funciones:
       *
       * osc()
       * noise()
       * src()
       * voronoi()
       * o0
       * s0
       * mouse
       * time
       *
       * están disponibles.
       */
      method()

      console.log(
        "Hydra reevaluado:",
        methodNames[0]
      )

    } catch (error) {
      console.error(
        "Error evaluando Hydra:",
        error
      )
    }
  }

  /*
   * ==========================================
   * LOADER
   * ==========================================
   */

  startLoader() {
    const frames = [
      "|",
      "/",
      "-",
      "\\"
    ]

    let i = 0

    this.textTarget.textContent =
      frames[0]

    this.loaderInterval =
      setInterval(() => {
        i =
          (i + 1) %
          frames.length

        this.textTarget.textContent =
          frames[i]
      }, 100)
  }

  stopLoader() {
    if (this.loaderInterval) {
      clearInterval(
        this.loaderInterval
      )

      this.loaderInterval = null
    }
  }
}